import React, {useEffect, useMemo, useState} from 'react'
import {
  StripeCardCvcElement,
  StripeCardExpiryElement,
  StripeCardNumberElement,
  StripeElementStyle,
} from '@stripe/stripe-js'
import {useElements} from '@stripe/react-stripe-js'

export default function CardFields(props: {
  disabled?: boolean
  onLoad: (element: StripeCardNumberElement) => void
  style?: StripeElementStyle
  spacing?: number
}) {
  const {disabled, onLoad, style} = props
  const elements = useElements()
  const [
    numberElement,
    setNumberElement,
  ] = useState<StripeCardNumberElement | null>(null)
  const [
    expiryElement,
    setExpiryElement,
  ] = useState<StripeCardExpiryElement | null>(null)
  const [cvcElement, setCvcElement] = useState<StripeCardCvcElement | null>(
    null,
  )
  const [numberDiv, setNumberDiv] = useState<HTMLDivElement | null>(null)
  const [expiryDiv, setExpiryDiv] = useState<HTMLDivElement | null>(null)
  const [cvcDiv, setCvcDiv] = useState<HTMLDivElement | null>(null)

  // Styles for element fields that are rendered inside Stripe's iframe.
  const stripeElementStyle: StripeElementStyle = useMemo(() => {
    if (style) {
      return style
    }

    return {
      base: {
        fontFamily: 'Rubik, sans-serif',
        fontSize: '16px',
        ':disabled': {
          color: 'rgba(0, 0, 0, 0.36)',
        },
        '::placeholder': {
          color: 'rgba(0, 0, 0, 0.36)',
        },
        color: '#000000',
      },
      invalid: {
        color: '#f44336',
      },
    }
  }, [style])

  // Load Card Fields
  useEffect(() => {
    if (!elements) {
      return
    }

    if (!numberDiv || !expiryDiv || !cvcDiv) {
      return
    }

    const numberElement =
      elements.getElement('cardNumber') ??
      elements.create('cardNumber', {
        style: stripeElementStyle,
      })
    numberElement.mount(numberDiv)
    setNumberElement(numberElement)

    const expiryElement =
      elements.getElement('cardExpiry') ??
      elements.create('cardExpiry', {
        style: stripeElementStyle,
      })
    expiryElement.mount(expiryDiv)
    setExpiryElement(expiryElement)

    const cvcElement =
      elements.getElement('cardCvc') ??
      elements.create('cardCvc', {
        style: stripeElementStyle,
      })
    cvcElement.mount(cvcDiv)
    setCvcElement(cvcElement)

    // Pass any element to parent component so that it can be referenced when
    // the card form is submitted.
    onLoad(numberElement)
    numberElement.focus()
  }, [
    elements,
    setNumberElement,
    numberDiv,
    expiryDiv,
    cvcDiv,
    onLoad,
    stripeElementStyle,
  ])

  // Handle disabled state
  useEffect(() => {
    if (!numberElement || !expiryElement || !cvcElement) {
      return
    }

    numberElement.update({disabled})
    expiryElement.update({disabled})
    cvcElement.update({disabled})
  }, [disabled, numberElement, expiryElement, cvcElement])

  return (
    <>
      <div ref={setNumberDiv} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridGap: '16px',
          marginTop: props.spacing,
        }}
      >
        <div ref={setExpiryDiv} />
        <div ref={setCvcDiv} />
      </div>
    </>
  )
}
